import React, { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { auth as authApi } from '@/utils/api';
import { User, Lock, Eye, EyeOff, Save, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

// Hoisted outside the parent so React doesn't remount the input on every keystroke
function PwField({ name, label, value, onChange, show, onToggleShow, autoComplete }) {
  return (
    <div>
      <label className="block text-xs font-semibold mb-1 uppercase tracking-wide" style={{ color: 'var(--muted)' }}>{label}</label>
      <div className="relative">
        <input
          className="input-agency text-sm pr-10 font-mono"
          type={show ? 'text' : 'password'}
          value={value}
          autoComplete={autoComplete}
          onChange={onChange}
          required
          data-testid={`profile-${name}-input`}
        />
        <button type="button" className="absolute right-3 top-1/2 -translate-y-1/2" onClick={onToggleShow}>
          {show ? <EyeOff size={14} style={{ color: 'var(--muted)' }} /> : <Eye size={14} style={{ color: 'var(--muted)' }} />}
        </button>
      </div>
    </div>
  );
}

export default function OperatorProfile() {
  const { user } = useAuth();
  const [form, setForm] = useState({ current: '', next: '', confirm: '' });
  const [show, setShow] = useState({});
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.next.length < 8) return toast.error('New password must be at least 8 characters.');
    if (form.next !== form.confirm) return toast.error('New passwords do not match.');
    setSaving(true);
    try {
      await authApi.changePassword(form.current, form.next);
      toast.success('Password updated. Use the new password next time you sign in.');
      setForm({ current: '', next: '', confirm: '' });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change password.');
    } finally { setSaving(false); }
  };

  return (
    <div className="p-6 max-w-2xl animate-fade-in" data-testid="operator-profile-page">
      <h1 className="text-xl font-black mb-1 flex items-center gap-2" style={{ color: 'var(--ink)' }}>
        <User size={18} /> Profile
      </h1>
      <p className="text-sm mb-6" style={{ color: 'var(--muted)' }}>Your operator account.</p>

      <div className="card-base p-5 mb-5">
        <p className="text-xs uppercase tracking-wider font-bold mb-2" style={{ color: 'var(--muted)' }}>Account</p>
        <div className="space-y-2">
          <div className="flex justify-between text-sm">
            <span style={{ color: 'var(--muted)' }}>Name</span>
            <span style={{ color: 'var(--ink)' }} className="font-semibold">{user?.name || '—'}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span style={{ color: 'var(--muted)' }}>Email</span>
            <span style={{ color: 'var(--ink)' }} className="font-mono">{user?.email}</span>
          </div>
          <div className="flex justify-between text-sm">
            <span style={{ color: 'var(--muted)' }}>Role</span>
            <span className="badge-status" style={{ background: '#5B2D8E15', color: '#5B2D8E' }}>{user?.role}</span>
          </div>
        </div>
      </div>

      <form onSubmit={submit} className="card-base p-5">
        <div className="flex items-center gap-2 mb-4">
          <Lock size={15} style={{ color: '#006080' }} />
          <p className="font-bold" style={{ color: 'var(--ink)' }}>Change password</p>
        </div>

        <div className="space-y-3">
          <PwField
            name="current" label="Current password" value={form.current}
            autoComplete="current-password"
            show={!!show.current} onToggleShow={() => setShow(s => ({ ...s, current: !s.current }))}
            onChange={e => setForm(f => ({ ...f, current: e.target.value }))}
          />
          <PwField
            name="next" label="New password (min 8 chars)" value={form.next}
            autoComplete="new-password"
            show={!!show.next} onToggleShow={() => setShow(s => ({ ...s, next: !s.next }))}
            onChange={e => setForm(f => ({ ...f, next: e.target.value }))}
          />
          <PwField
            name="confirm" label="Confirm new password" value={form.confirm}
            autoComplete="new-password"
            show={!!show.confirm} onToggleShow={() => setShow(s => ({ ...s, confirm: !s.confirm }))}
            onChange={e => setForm(f => ({ ...f, confirm: e.target.value }))}
          />
        </div>

        <button
          type="submit"
          disabled={saving || !form.current || !form.next || !form.confirm}
          className="btn-primary mt-4 flex items-center gap-2 px-4 py-2 text-sm"
          data-testid="profile-save-password"
        >
          {saving ? 'Saving…' : <><Save size={13} /> Update password</>}
        </button>

        <p className="text-xs mt-3 flex items-start gap-1.5" style={{ color: 'var(--muted)' }}>
          <CheckCircle2 size={11} className="flex-shrink-0 mt-0.5" />
          You'll stay signed in. Use the new password next time you log in.
        </p>
      </form>
    </div>
  );
}
